/* eslint-disable react-hooks/exhaustive-deps */

import React, { FC, useState, useEffect, MouseEvent } from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import triggerTracking from '@blueprint/tracking';
import { Text } from '@blueprint/ui';

import envelopeSvg from '../../public/assets/search/envelope.svg';
import {
    Content,
    StyledButtonLink,
    StyledHeading,
    StyledIcon,
    StyledImage
} from './AlertCtaModal.styles';
import { selectPageNumber } from '../../reducers/searchResults';
import { alertLinkClickHandler } from '../common/alertLinkClickHandler';
import { Links } from '../SearchContext';
import { useIsLoggedIn } from '../../shared/hooks/useIsLoggedIn';
import { StyledModal } from 'search/AlertPanel/AlertPanel.styles';

const triggerAlertPopupTracking = (eventLabel: string) => {
    triggerTracking({
        event: 'GAEvent',
        eventCategory: 'Alert pop up',
        eventAction: 'clickthrough',
        eventLabel
    });
};

type AlertCtaModalProps = {
    links: Links;
};

export const AlertCtaModal: FC<AlertCtaModalProps> = ({ links }) => {
    const pageNumber = useSelector(selectPageNumber);
    const isLoggedIn = useIsLoggedIn();

    const [isModalShowing, setIsModalShowing] = useState(false);
    const [willShowModal, setWillShowModal] = useState(false);
    const router = useRouter();

    useEffect(() => {
        const showModal =
            pageNumber >= 2 &&
            isLoggedIn === false &&
            typeof window !== 'undefined' &&
            !sessionStorage.getItem('alertsPopupClose');
        setWillShowModal(showModal);
    }, [
        pageNumber,
        isLoggedIn,
        typeof window !== 'undefined' &&
            sessionStorage.getItem('alertsPopupClose')
    ]);

    useEffect(() => {
        setIsModalShowing(sessionStorage.getItem('alertsPopupClose') === null);
    }, []);

    useEffect(() => {
        if (willShowModal) {
            triggerAlertPopupTracking('Alert pop up open');
        }
    }, [willShowModal]);

    if (!willShowModal || !isModalShowing) {
        return null;
    }

    const closeModal = () => {
        setIsModalShowing(false);
        sessionStorage.setItem('alertsPopupClose', '1');
    };

    const onButtonClick = (evt: MouseEvent<HTMLAnchorElement>) => {
        evt.preventDefault();

        triggerAlertPopupTracking('Alert pop up click');

        sessionStorage.setItem('alertsPopupClose', '1');
        alertLinkClickHandler({
            evt,
            router,
            requiresAuth: true,
            loggedIn: !!isLoggedIn
        });
    };

    return (
        <StyledModal isShowing={isModalShowing} close={closeModal}>
            <Content>
                <StyledImage src={envelopeSvg} alt="Envelope image" />
                <StyledHeading size="5">Sign up for email alerts</StyledHeading>
                <Text size="secondary">
                    Be the first to know about new properties matching your
                    search criteria
                </Text>
                <StyledButtonLink
                    onClick={onButtonClick}
                    data-url={links?.createAlert || ''}
                >
                    Create alert
                </StyledButtonLink>
                <Text size="small">
                    <StyledIcon iconId="padlock" />
                    We will not share your details with third parties
                </Text>
            </Content>
        </StyledModal>
    );
};
